import React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

interface _ListingItemProps {
    id: number
    imageUrl: string
    canvasSrc?: string
    originalSrc?: string
    imageAlt?: string
    isDraggable?: boolean
    itemId: number
    collectionSlug: string
    type?: 'existing-image' | 'new-image'
    className?: string
    contentType?: 'file' | 'product' | 'external'
    isInCanvas?: boolean
    isCover?: boolean
    onCoverToggle?: () => void
    showCoverToggle?: boolean
    mimeType?: string
}

const _ListingItem: React.FC<_ListingItemProps> = React.memo(
    ({
        id,
        imageUrl,
        canvasSrc,
        originalSrc,
        imageAlt,
        isDraggable = false,
        itemId,
        collectionSlug,
        contentType,
        type = 'existing-image',
        className = 'col-md-4 mb-4 grid-item',
        isInCanvas = false,
        isCover = false,
        onCoverToggle,
        showCoverToggle = false,
        mimeType,
    }) => {
        const { t } = useTranslation()
        const isVideo = mimeType?.startsWith('video/') || imageUrl?.match(/\.(mp4|webm|ogg|mov|m4v|avi|flv)(.*)?$/i)
        const itemType: 'FILE' | 'PRODUCT' | 'EXTERNAL_IMAGE' = contentType === 'file' ? 'FILE' : contentType === 'external' ? 'EXTERNAL_IMAGE' : 'PRODUCT'

        const { attributes, listeners, setNodeRef, transform, isDragging } =
            useDraggable({
                id: id || `image-${Date.now()}`,
                data: {
                    type: type,
                    itemType,
                    imageUrl: canvasSrc ?? imageUrl,
                    originalSrc: originalSrc ?? canvasSrc ?? imageUrl,
                    previewSrc: imageUrl,
                    imageAlt,
                    itemId,
                    collectionSlug,
                    mimeType,
                },
                disabled: !isDraggable,
            })

        const style = transform
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 9999 }
            : undefined

        const getFileName = (url: string) => {
            const lastSegment = url.split('/').pop() || 'untitled'
    
            return lastSegment.split('?')[0]
        }

        const getTooltipText = () =>
            imageAlt && /\.[a-z0-9]+$/i.test(imageAlt) ? imageAlt : getFileName(imageUrl)

        const renderTooltip = (props: any) => (
            <Tooltip id={`tooltip-${id}`} {...props}>
                {getTooltipText()}
            </Tooltip>
        )

        const CanvasIndicator = () =>
            isInCanvas ? (
                <div className="canvas-indicator" title="Item is on the canvas">
                    <i className="fa-solid fa-circle-check"></i>
                </div>
            ) : null

        const CoverIndicator = () =>
            showCoverToggle ? (
                <button
                    type="button"
                    className={`cover-toggle ${isCover ? 'active' : ''}`}
                    title={t(isCover ? 'app.studio.unset_cover_image' : 'app.studio.set_cover_image')}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation()
                        onCoverToggle?.()
                    }}
                >
                    <i className={`fa-star ${isCover ? 'fa-solid' : 'fa-regular'}`}></i>
                </button>
            ) : null

        const draggableClass = isDraggable ? 'draggable' : 'non-draggable'
        const draggingClass = isDragging ? 'dragging' : ''

        if (isDraggable) {
            return (
                <div
                    ref={setNodeRef}
                    className={`${className} listing-item ${draggableClass} ${draggingClass}`}
                    style={style}
                >
                    <CanvasIndicator />
                    <CoverIndicator />
                    <OverlayTrigger placement="bottom" overlay={renderTooltip}>
                        <div {...listeners} {...attributes} className="item-wrapper">
                            {isVideo ? (
                                <video src={imageUrl} muted loop playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                            ) : (
                                <img src={imageUrl} alt={imageAlt || 'item'} loading="lazy" decoding="async" />
                            )}
                        </div>
                    </OverlayTrigger>
                </div>
            )
        }

        return (
            <OverlayTrigger placement="top" overlay={renderTooltip}>
                <div className={`${className} listing-item non-draggable`}>
                    <CanvasIndicator />
                    <CoverIndicator />
                    {isVideo ? (
                        <video src={imageUrl} className="rounded img-fluid z-3" muted loop playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <img src={imageUrl} className="rounded img-fluid z-3" alt={imageAlt || 'item'} loading="lazy" decoding="async" />
                    )}
                </div>
            </OverlayTrigger>
        )
    }
)

export default _ListingItem
